import { createApp, type App } from 'vue'
import type {
  SheetCellValue,
  SheetColumnCustomEditor,
  SheetWorksheetInstance
} from '@/components/designSystem/Cards/attendanceSheet.types'
import type {
  DSAttendanceDoctorSuggestion,
  DSAttendanceDoctorSuggestionField
} from '@/Network/Types/Responses/DsAttendancePolicyResponses'
import DoctorSuggestionCell from '../components/DoctorSuggestionCell.vue'
import { formatCrm } from './crm-format'

/**
 * RF-03/RF-04 do PRD 32 — reintegra o autocomplete de CRM/nome (#2231) como editor
 * customizado do jspreadsheet (mecanismo `CustomEditor`, gap conhecido registrado no
 * PR #2269 da US-06): a engine `native` (flag desligada) nunca chega a usar este editor,
 * porque `AttendanceSheetCard` só aplica `columnEditors` na engine `spreadsheet`.
 */

function formatFieldValue(
  field: DSAttendanceDoctorSuggestionField,
  suggestion: DSAttendanceDoctorSuggestion
) {
  return field === 'crm' ? formatCrm(suggestion) : suggestion.name
}

/** RF-04 — o realce só torna a substituição visível; não bloqueia nem pede confirmação. */
const HIGHLIGHT_DURATION_MS = 1600

function highlightCell(instance: SheetWorksheetInstance, x: number, y: number) {
  const cell = instance.getCell(x, y) as HTMLTableCellElement | null
  if (!cell) return
  cell.classList.add('sheet-cell-highlight')
  setTimeout(() => cell.classList.remove('sheet-cell-highlight'), HIGHLIGHT_DURATION_MS)
}

export type PairedColumn = {
  index: number
  field: DSAttendanceDoctorSuggestionField
}

/**
 * Como a edição começa, a partir do evento que a abriu (7º argumento de `openEditor` — o
 * único sinal que a lib repassa; o booleano interno que diferencia F2/espaço/digitação é
 * descartado antes de chegar aqui, confirmado no dist da `jspreadsheet-ce`).
 *
 * - digitou um caractere → começa com ESSE caractere e nada do valor salvo. Começar vazio
 *   (comportamento anterior) perdia o 1º caractere digitado (apontamento 1, vídeo 03/09);
 *   começar com o valor salvo concatenava o texto novo com o antigo (UAT-26).
 * - `Process` (composição IME/CJK) → começa vazio: `key` não carrega o caractere final, e a
 *   composição escreve no input por conta própria.
 * - F2, Espaço, duplo clique → preservam o valor salvo, como sempre. `key === ' '` tem
 *   length 1 mas a lib o trata como "abrir preservando", não como digitação.
 */
export function resolveEditorSeed(
  value: SheetCellValue | undefined,
  triggerEvent?: Event
): { initialValue: string; typedCharacter: string } {
  if (!(triggerEvent instanceof KeyboardEvent)) {
    return { initialValue: String(value ?? ''), typedCharacter: '' }
  }

  const { key } = triggerEvent
  if (key === 'Process') return { initialValue: '', typedCharacter: '' }
  if (key.length === 1 && key !== ' ') return { initialValue: key, typedCharacter: key }

  return { initialValue: String(value ?? ''), typedCharacter: '' }
}

/**
 * Um editor por coluna (CRM ou nome). `pairedColumn`, quando presente, é a outra coluna
 * do cruzamento (RF-04) — `null` quando o modelo de folha só tem uma das duas (RF-04:
 * "se o modelo contém apenas uma das duas colunas, o autocomplete funciona nela e o
 * cruzamento não se aplica").
 */
export function createDoctorSuggestionCellEditor(
  field: DSAttendanceDoctorSuggestionField,
  pairedColumn: PairedColumn | null,
  getContainerId: () => string
): SheetColumnCustomEditor {
  let app: App | null = null
  /**
   * Célula que o `app` vivo está editando. Existe para a guarda de reentrância do
   * `openEditor` (N8/#2359): a lib pode reinvocá-lo durante a digitação, e sem saber *qual*
   * célula está em edição não há como distinguir "reabriram a mesma" de "pularam para outra".
   */
  let activeCell: HTMLTableCellElement | null = null
  /** Valor atual do editor — o que `closeEditor` devolve quando `save` é verdadeiro. */
  let currentValue = ''
  /**
   * A seleção de uma sugestão já grava as duas células diretamente via `setValue` em
   * lote (RN-07: um único passo de desfazer) e chama `closeEditor(cell, false)` só para
   * o jspreadsheet encerrar o estado de edição — o `closeEditor` desta classe não deve
   * tocar o conteúdo da célula nesse caminho, só desmontar o app Vue.
   */
  let skipCellRenderOnClose = false

  function teardownApp() {
    if (app) {
      app.unmount()
      app = null
    }
    activeCell = null
  }

  return {
    openEditor(cell, value, x, y, instance: SheetWorksheetInstance, _columnConfig, triggerEvent) {
      // N8/#2359 — guarda de reentrância. A lib reinvoca `openEditor` enquanto a edição
      // está em curso (observado na abertura por digitação), e cada reinvocação reexecutava
      // tudo abaixo: `currentValue` voltava ao valor de abertura — `''` no caminho
      // type-to-open — e um app novo era montado, levando com ele os caracteres já
      // digitados. A perda era proporcional à velocidade de digitação (UAT-16/21/24).
      //
      // Reabrir a MESMA célula é no-op: o editor vivo já é o estado correto. Célula
      // diferente sem `closeEditor` no meio desmonta o anterior primeiro — `cell.innerHTML`
      // abaixo limpa a célula, mas não o popover do app antigo, que é `Teleport` para
      // `body` e sobreviveria montado, com a âncora já destacada do DOM.
      if (app) {
        if (cell === activeCell) return
        teardownApp()
      }
      activeCell = cell

      // UAT-26 + apontamento 1 — como a edição começa depende do evento que a abriu; a
      // decisão inteira vive em `resolveEditorSeed` (documentada lá, e testável sem grade).
      const { initialValue, typedCharacter } = resolveEditorSeed(value, triggerEvent)
      currentValue = initialValue
      skipCellRenderOnClose = false
      cell.innerHTML = ''
      cell.classList.add('editor')

      const container = document.createElement('div')
      cell.appendChild(container)

      app = createApp(DoctorSuggestionCell, {
        modelValue: currentValue,
        containerId: getContainerId(),
        field,
        columnKey: String(instance.options.columns?.[x]?.name ?? x),
        rowIndex: y,
        'onUpdate:modelValue': (next: string) => {
          currentValue = next
        },
        onSelect: (suggestion: DSAttendanceDoctorSuggestion) => {
          const ownValue = formatFieldValue(field, suggestion)
          const records: { x: number; y: number; value: SheetCellValue }[] = [
            { x, y, value: ownValue }
          ]
          if (pairedColumn) {
            records.push({
              x: pairedColumn.index,
              y,
              value: formatFieldValue(pairedColumn.field, suggestion)
            })
          }

          // Desmonta antes de escrever: o app Vue ainda ocupa o DOM da célula, e
          // `setValue` vai re-renderizá-la diretamente a seguir.
          teardownApp()
          skipCellRenderOnClose = true
          // `closeEditor(cell, false)` da lib termina restaurando `edition[1]` (o
          // innerHTML capturado na abertura) no `cell.innerHTML` — incondicionalmente,
          // mesmo tendo chamado o hook `closeEditor` desta classe antes. `setValue` só
          // depois de fechar garante que o valor final escrito no DOM (via `updateCell`)
          // não seja sobrescrito por esse restore.
          instance.closeEditor(cell, false)
          // RN-07 (#2115) — as duas células (própria + par) são escritas numa única
          // chamada em lote, que o jspreadsheet grava como um único registro de
          // histórico: a seleção vira um único passo de desfazer para as duas colunas.
          instance.setValue(records)

          // RF-04 — destaque transitório nas duas células alteradas, depois de escritas.
          for (const record of records) highlightCell(instance, record.x, record.y)
        }
      })

      app.mount(container)
      // Consulta direto no container montado, não em `$el` da instância: o SFC tem dois
      // nós raiz (`input` + `Teleport`), e `$el` de um fragmento não é confiável aqui.
      const input = container.querySelector('input')
      input?.focus()

      // Apontamento 1 — o caractere semeado entra pelo MESMO caminho de uma tecla digitada
      // (evento `input`), para que a busca de sugestões dispare como sempre em vez de a
      // lista só aparecer no segundo caractere.
      if (input && typedCharacter) {
        input.value = typedCharacter
        input.dispatchEvent(new Event('input', { bubbles: true }))
        input.setSelectionRange(typedCharacter.length, typedCharacter.length)
      }
    },
    // `updateCell` — sem este hook, toda escrita fora de `closeEditor(save=true)`
    // (`setValue`/`setValueFromCoords`/`undo`/`redo`) só atualiza `options.data`, nunca o
    // DOM (lib grava a célula apenas quando `type` é objeto E define `updateCell`). Não
    // chama nada da instância aqui: este hook roda também dentro de `undo()`/`redo()`, que
    // já estão com `ignoreEvents`/`ignoreHistory` ligados. `cell.textContent =` substitui
    // todo o conteúdo da célula, inclusive um container de app Vue eventualmente esquecido.
    updateCell(cell, value) {
      cell.textContent = String(value ?? '')
      return undefined
    },
    closeEditor(cell, save, x, y, instance) {
      const value = currentValue
      const shouldSkipRender = skipCellRenderOnClose
      skipCellRenderOnClose = false
      teardownApp()

      // A seleção já deixou a célula com o conteúdo certo via `setValue` — não sobrescrever.
      if (shouldSkipRender) return undefined

      if (!save) {
        // RF-10 — cancelar a edição (Escape) devolve a célula ao que já estava salvo; o
        // jspreadsheet não re-renderiza sozinho colunas com editor customizado.
        cell.textContent = String(instance.options.data?.[y]?.[x] ?? '')
        return undefined
      }

      return value
    }
  }
}
